import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Paper, Typography, Alert, Chip, Card, CardContent, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, TablePagination,
} from '@mui/material';
import { VideoCall, Link as LinkIcon, Google } from '@mui/icons-material';
import { googleMeetService, userService } from '../services/api';
import { format } from 'date-fns';
import FeatureToggleSwitch from './FeatureToggleSwitch';
import { usePagination } from '../hooks/usePagination';

const mappingColor = (s: string) => (s === 'Matched' ? 'success' : s === 'Ambiguous' ? 'warning' : 'default');
const meetingStatusColor = (s: string) =>
  s === 'COMPLETED' ? 'success' : s === 'PARTIAL' || s === 'JOINED' ? 'warning' : s === 'AMBIGUOUS' ? 'error' : 'default';

const GoogleMeetAdmin: React.FC = () => {
  const [status, setStatus] = useState<any>(null);
  const [daily, setDaily] = useState<any>(null);
  const [report, setReport] = useState<any[]>([]);
  const [message, setMessage] = useState('');
  const [msgSeverity, setMsgSeverity] = useState<'info' | 'success' | 'error'>('info');
  const [loading, setLoading] = useState(false);

  // Ambiguous-participant resolution dialog
  const [resolveTarget, setResolveTarget] = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  const showMsg = (text: string, severity: 'info' | 'success' | 'error' = 'info') => {
    setMessage(text);
    setMsgSeverity(severity);
    setTimeout(() => setMessage(''), 5000);
  };

  const loadStatus = useCallback(async () => {
    try {
      const data = await googleMeetService.getStatus();
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, []);

  const loadDailyAndReport = useCallback(async () => {
    try {
      const meeting = await googleMeetService.getDaily();
      setDaily(meeting);
      if (meeting?.id) {
        const rows = await googleMeetService.getReport(meeting.id);
        setReport(rows || []);
      } else {
        setReport([]);
      }
    } catch {
      setDaily(null);
      setReport([]);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadDailyAndReport();
    userService.getEmployees().then(setEmployees).catch(() => {});
  }, [loadStatus, loadDailyAndReport]);

  const handleConnect = async () => {
    try {
      const { authorizationUrl } = await googleMeetService.connect();
      window.open(authorizationUrl, '_blank', 'noopener,noreferrer,width=600,height=700');
      showMsg('Complete the Google sign-in in the new window, then click "Refresh Status".', 'info');
    } catch (error: any) {
      showMsg(error.response?.data?.message || 'Failed to start Google authorization.', 'error');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect the Google Meet integration? Employees will lose meeting access until it is reconnected.')) return;
    try {
      await googleMeetService.disconnect();
      showMsg('Google Meet disconnected.', 'success');
      loadStatus();
    } catch (error: any) {
      showMsg(error.response?.data?.message || 'Failed to disconnect.', 'error');
    }
  };

  const handleCreateDaily = async () => {
    setLoading(true);
    try {
      await googleMeetService.createDaily();
      showMsg('Today\'s meeting is ready.', 'success');
      loadDailyAndReport();
    } catch (error: any) {
      showMsg(error.response?.data?.message || 'Failed to create today\'s meeting.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openResolveDialog = (row: any) => {
    setResolveTarget(row);
    setSelectedEmployeeId('');
  };

  const handleResolve = async () => {
    if (!resolveTarget || !selectedEmployeeId) return;
    try {
      await googleMeetService.resolveAmbiguousParticipant(resolveTarget.rawDisplayName, resolveTarget.rawGoogleUserId, Number(selectedEmployeeId));
      showMsg('Participant resolved.', 'success');
      setResolveTarget(null);
      loadDailyAndReport();
    } catch (error: any) {
      showMsg(error.response?.data?.message || 'Failed to resolve participant.', 'error');
    }
  };

  const totalDurationSeconds = report.reduce((sum, r) => sum + (r.totalDurationSeconds || 0), 0);
  const { pagedRows: pagedReport, paginationProps: reportPaginationProps } = usePagination(report);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">Google Meet Integration</Typography>

      {message && <Alert severity={msgSeverity} sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <FeatureToggleSwitch
        featureKey="GoogleMeet"
        label="Google Meet Integration"
        description="When off, employees cannot access Google Meet from the Attendance page and the daily-meeting background job pauses. Toggling here takes effect immediately, no deploy needed."
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Google fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">Connection</Typography>
            </Stack>
            <Chip
              label={status?.connected ? 'CONNECTED' : 'NOT CONNECTED'}
              color={status?.connected ? 'success' : 'default'}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2">
              {status?.connected ? `Account: ${status.organizerEmail}` : 'No Google Workspace account connected yet.'}
            </Typography>
            {status?.lastError && <Alert severity="warning" sx={{ mt: 1 }}>{status.lastError}</Alert>}
            <Stack direction="row" spacing={1} mt={2}>
              <Button size="small" variant="contained" onClick={handleConnect}>
                {status?.connected ? 'Reconnect Google Account' : 'Connect Google Account'}
              </Button>
              <Button size="small" variant="outlined" onClick={loadStatus}>Refresh Status</Button>
              {status?.connected && (
                <Button size="small" color="error" variant="outlined" onClick={handleDisconnect}>Disconnect</Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <VideoCall fontSize="small" />
              <Typography variant="subtitle2" color="text.secondary">Today's Meeting</Typography>
            </Stack>
            <Chip
              label={daily ? daily.status?.toUpperCase() : 'NOT CREATED'}
              color={daily?.status === 'Active' ? 'success' : daily ? 'warning' : 'default'}
              sx={{ mb: 1 }}
            />
            {daily ? (
              <>
                <Typography variant="body2">Code: {daily.meetingCode}</Typography>
                <Typography variant="body2">Participants today: {daily.participantCount}</Typography>
                {daily.meetingUri && (
                  <Button size="small" startIcon={<LinkIcon />} href={daily.meetingUri} target="_blank" rel="noopener noreferrer" sx={{ mt: 1 }}>
                    Open Meeting
                  </Button>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">No meeting created for today yet.</Typography>
            )}
            <Box mt={2}>
              <Button size="small" variant="contained" onClick={handleCreateDaily} disabled={loading || !status?.connected}>
                {daily ? 'Recheck / Create Today\'s Meeting' : 'Create Today\'s Meeting'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            Today's Participation Report {daily ? `— ${format(new Date(daily.meetingDate), 'dd MMM yyyy')}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total meeting time across all participants: {Math.round(totalDurationSeconds / 60)} min
          </Typography>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Employee', 'Attendance', 'Joined', 'First Join', 'Last Leave', 'Sessions', 'Total Duration', 'Identity', 'Status', 'Actions'].map(h => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {report.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 3, color: 'text.secondary' }}>No participation recorded yet today.</TableCell></TableRow>
            ) : pagedReport.map((row, idx) => (
              <TableRow key={idx} hover>
                <TableCell>{row.employeeName}</TableCell>
                <TableCell>{row.attendanceStatus}</TableCell>
                <TableCell>{row.joined ? 'Yes' : 'No'}</TableCell>
                <TableCell>{row.firstJoin ? format(new Date(row.firstJoin), 'HH:mm') : '—'}</TableCell>
                <TableCell>{row.lastLeave ? format(new Date(row.lastLeave), 'HH:mm') : '—'}</TableCell>
                <TableCell>{row.sessionCount}</TableCell>
                <TableCell>{Math.round((row.totalDurationSeconds || 0) / 60)} min</TableCell>
                <TableCell>
                  <Chip label={row.mappingStatus} color={mappingColor(row.mappingStatus) as any} size="small" />
                </TableCell>
                <TableCell>
                  <Chip label={row.meetingStatus} color={meetingStatusColor(row.meetingStatus) as any} size="small" />
                </TableCell>
                <TableCell>
                  {row.mappingStatus === 'Ambiguous' && (
                    <Button size="small" onClick={() => openResolveDialog(row)}>Resolve</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {report.length > 0 && <TablePagination {...reportPaginationProps} />}
      </TableContainer>

      <Dialog open={!!resolveTarget} onClose={() => setResolveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Resolve Ambiguous Participant</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            "{resolveTarget?.employeeName}" matched more than one employee by name. Select who this actually was:
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Employee</InputLabel>
            <Select value={selectedEmployeeId} label="Employee" onChange={e => setSelectedEmployeeId(e.target.value)}>
              {employees.map((e: any) => (
                <MenuItem key={e.id} value={e.id}>{e.fullName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleResolve} disabled={!selectedEmployeeId}>Resolve</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoogleMeetAdmin;
